import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  History,
  ShieldOff,
  Vote as VoteIcon,
} from "lucide-react";
import {
  api,
  type BoardQueue,
  type BoardQueueActiveRow,
  type BoardQueueRecentRow,
} from "../api";
import { Card, PageHeader, StatusBadge, cx } from "../ui";
import { useAuth } from "../auth";

const VOTE_LABEL: Record<string, string> = {
  approve: "Approved",
  conditionally_approve: "Conditional",
  reject: "Rejected",
  request_revisions: "Revisions",
  abstain: "Abstained",
  recuse: "Recused",
};

export function BoardPage() {
  const { state } = useAuth();
  const [queue, setQueue] = useState<BoardQueue | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<BoardQueue>("/api/board/queue")
      .then(setQueue)
      .catch((err) => setError(String(err)));
  }, []);

  const eyebrow =
    state.status === "authenticated"
      ? `${state.me.user.firstName} ${state.me.user.lastName}`
      : undefined;

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title="Board Approval"
        subtitle="Projects awaiting your approval, the ones you've already reviewed, and the most recent decisions of your Board. The system never shows how other Board members approval status, even after a step finalizes."
      />

      {error && <div className="alert-error mb-4">{error}</div>}

      {queue === null ? (
        <Card>
          <p className="text-ink-500">Loading…</p>
        </Card>
      ) : (
        <>
          <BallotSection
            title="Awaiting your review"
            icon={<VoteIcon size={16} />}
            empty="You're caught up — no reviews pending."
            tone="now"
            rows={queue.awaitingMyVote}
          />
          <BallotSection
            title="Already reviewed (you can change)"
            icon={<CheckCircle2 size={16} />}
            empty="No reviews cast on open steps."
            tone="done"
            rows={queue.alreadyVoted}
          />
          <RecentSection rows={queue.recentlyDecided} />
        </>
      )}
    </div>
  );
}

function BallotSection({
  title,
  icon,
  empty,
  tone,
  rows,
}: {
  title: string;
  icon: React.ReactNode;
  empty: string;
  tone: "now" | "done";
  rows: BoardQueueActiveRow[];
}) {
  return (
    <Card>
      <h2 className="text-ink-800 m-0 mb-3 inline-flex items-center gap-2">
        {icon} {title}
        <span
          className={cx(
            "inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold",
            tone === "now"
              ? "bg-brand-100 text-brand-700"
              : "bg-sand-100 text-ink-600",
          )}
        >
          {rows.length}
        </span>
      </h2>
      {rows.length === 0 ? (
        <p className="text-[13px] text-ink-500">{empty}</p>
      ) : (
        <ul className="divide-y divide-sand-100">
          {rows.map((r) => (
            <BallotRow key={r.approvalStepId} row={r} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function BallotRow({ row }: { row: BoardQueueActiveRow }) {
  const remaining = useMemo(() => {
    if (!row.votingWindowEndsAt) return null;
    const ms = new Date(row.votingWindowEndsAt).getTime() - Date.now();
    if (ms <= 0) return { days: 0, label: "Closed", tone: "rejected" as const };
    const days = Math.ceil(ms / 86_400_000);
    const tone =
      days <= 2
        ? ("rejected" as const)
        : days <= 5
          ? ("warn" as const)
          : ("ok" as const);
    return { days, label: `${days}d left`, tone };
  }, [row.votingWindowEndsAt]);

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {row.isEmergency && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-status-rejected-bg text-status-rejected-fg text-[11px] font-semibold">
                <AlertTriangle size={11} />
                Emergency
              </span>
            )}
            {row.autoRecusedHere && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sand-100 text-ink-600 text-[11px] font-semibold"
                title="You're auto-recused on this project (you own the unit)"
              >
                <ShieldOff size={11} />
                Auto-recused
              </span>
            )}
            {row.myVote && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-status-approved-bg text-status-approved-fg text-[11px] font-semibold">
                Your vote: {VOTE_LABEL[row.myVote] ?? row.myVote}
              </span>
            )}
            {remaining && (
              <span
                className={cx(
                  "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold",
                  remaining.tone === "rejected"
                    ? "bg-status-rejected-bg text-status-rejected-fg"
                    : remaining.tone === "warn"
                      ? "bg-status-review-bg text-status-review-fg"
                      : "bg-sand-100 text-ink-600",
                )}
                title={
                  row.votingWindowEndsAt
                    ? `Closes ${new Date(row.votingWindowEndsAt).toLocaleString()}`
                    : undefined
                }
              >
                <Clock size={11} />
                {remaining.label}
              </span>
            )}
          </div>
          <Link
            to={`/projects/${row.projectId}`}
            className="text-ink-800 font-semibold hover:text-brand-700 no-underline hover:no-underline text-[15px]"
          >
            {row.projectTitle}
          </Link>
          <div className="text-[12.5px] text-ink-500 mt-1">
            {row.typeName} · Unit {row.unitNumber} ·{" "}
            <span className="text-ink-700">
              {row.votesCast} of {row.eligibleVoters} votes cast
            </span>
          </div>
        </div>
        <Link
          to={`/projects/${row.projectId}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-[13px] text-brand-700 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-md no-underline hover:no-underline whitespace-nowrap"
        >
          {row.myVote ? "Review / change" : "Cast vote"}
          <ArrowRight size={13} />
        </Link>
      </div>
    </li>
  );
}

function RecentSection({ rows }: { rows: BoardQueueRecentRow[] }) {
  return (
    <Card>
      <h2 className="text-ink-800 m-0 mb-3 inline-flex items-center gap-2">
        <History size={16} /> Recently decided
        <span className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold bg-sand-100 text-ink-600">
          {rows.length}
        </span>
      </h2>
      {rows.length === 0 ? (
        <p className="text-[13px] text-ink-500">
          No recently finalized Board decisions you were eligible on.
        </p>
      ) : (
        <ul className="divide-y divide-sand-100">
          {rows.map((r) => (
            <li
              key={r.approvalStepId}
              className="py-2.5 flex items-center justify-between gap-3 flex-wrap"
            >
              <div className="flex-1 min-w-0">
                <Link
                  to={`/projects/${r.projectId}`}
                  className="text-ink-800 font-medium hover:text-brand-700 no-underline hover:no-underline"
                >
                  {r.projectTitle}
                </Link>
                <div className="text-[12px] text-ink-500 mt-0.5">
                  {r.typeName} · Unit {r.unitNumber} · decided{" "}
                  {new Date(r.decidedAt).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.myVote && (
                  <span className="text-[11.5px] text-ink-500">
                    You: {VOTE_LABEL[r.myVote] ?? r.myVote}
                  </span>
                )}
                <StatusBadge status={r.decision} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
